// src/layouts/configuration/index.js
import React, { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import { Card, Tab, Tabs, CircularProgress, Box } from '@mui/material';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import ArgonBox from 'components/ArgonBox';
import ArgonSelect from 'components/ArgonSelect';
import Footer from 'examples/Footer';

// --- Child Tab Components ---
import PricingRules from './components/pricingRules';
import RevenueBilling from './components/revenueBilling';
import Approval from './components/approval';
import Tasks from './components/tasks';
import Quote from './components/quote';

// --- Contexts & Services ---
import { UserContext } from 'context/userContext';
import { OrgContext } from 'context/orgContext';
import { useNotifications } from 'context/NotificationContext';
import { userOrgs } from 'services/org';
import { getCPQQuoteTemplates } from 'services/quotes';
import { getCPQPriceRules } from 'services/priceRule';
import { useVisibility } from 'context/visibilityContext';

function Configuration() {
  const { userId } = useContext(UserContext);
  const { orgs, setOrgs } = useContext(OrgContext);
  const { addNotification } = useNotifications();
  const { isLoadingConfig } = useVisibility();

  const [activeTab, setActiveTab] = useState(0);
  const [loadingOrgs, setLoadingOrgs] = useState(true); // Start true
  const [quoteTemplates, setQuoteTemplates] = useState([]);
  const [priceRules, setPriceRules] = useState([]);
  const [loadingRelatedData, setLoadingRelatedData] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState('');

  // --- REFACTORED: fetchOrgs function ---
  const fetchOrgs = useCallback(async () => {
    if (!userId) return; // Guard against running without a user
    setLoadingOrgs(true);
    try {
      // NEW PATTERN: Service returns data directly or throws. No userId needed.
      const { orgs: fetchedOrgs } = await userOrgs();
      setOrgs(fetchedOrgs || []);
    } catch (error) {
      addNotification({ title: ['Error Fetching Orgs'], details: error.message, severity: 'error' });
      setOrgs([]);
    } finally {
      setLoadingOrgs(false);
    }
  }, [userId, setOrgs, addNotification]);

  useEffect(() => {
    // Fetch orgs only if they are not already in the context
    if (!orgs || orgs.length === 0) {
      fetchOrgs();
    } else {
      setLoadingOrgs(false); // Orgs are already loaded
    }
  }, [orgs, fetchOrgs]);

  const orgOptions = useMemo(() => {
    if (!orgs) return [];
    // Filter for CPQ orgs for this specific configuration page
    return orgs.filter((org) => org.type === 'CPQ').map((org) => ({ value: org.org_id, label: org.name }));
  }, [orgs]);

  // --- REFACTORED: fetchRelatedData function ---
  const fetchRelatedData = useCallback(
    async (orgId) => {
      if (!orgId) return;
      setLoadingRelatedData(true);
      setQuoteTemplates([]);
      setPriceRules([]);
      try {
        // Fetch in parallel for better performance
        const [templatesResponse, rulesResponse] = await Promise.all([
          getCPQQuoteTemplates(orgId), // No userId needed
          getCPQPriceRules(orgId), // No userId needed
        ]);

        setQuoteTemplates(templatesResponse.quoteTemplates || []);
        setPriceRules(rulesResponse.priceRules || []);
      } catch (error) {
        addNotification({ title: ['Error Fetching Configuration'], details: error.message, severity: 'error' });
        setQuoteTemplates([]);
        setPriceRules([]);
      } finally {
        setLoadingRelatedData(false);
      }
    },
    [addNotification]
  );

  const handleOrgChange = (e) => {
    const newOrgId = e.target.value;
    setSelectedOrgId(newOrgId);
    if (newOrgId) {
      const selectedLabel = orgOptions.find((opt) => opt.value === newOrgId)?.label || newOrgId;
      addNotification({
        title: ['Info', 'Organization Changed'],
        severity: 'info',
        details: `Fetching configurations for: ${selectedLabel}`,
      });
      fetchRelatedData(newOrgId);
    } else {
      // Clear data if no org is selected
      setQuoteTemplates([]);
      setPriceRules([]);
    }
  };

  const handleTabChange = (event, newValue) => setActiveTab(newValue);

  const selectedOrgType = useMemo(() => {
    if (!selectedOrgId || !orgs) return 'cpq';
    const selectedOrg = orgs.find((org) => org.org_id === selectedOrgId);
    return selectedOrg?.type?.toLowerCase() || 'cpq';
  }, [selectedOrgId, orgs]);

  const childProps = {
    orgs: orgs || [],
    loading: loadingOrgs || isLoadingConfig || loadingRelatedData,
    selectedOrgId: selectedOrgId,
    orgType: selectedOrgType.toLowerCase(),
  };

  const renderActiveTab = () => {
    if (childProps.loading && !selectedOrgId) {
      return (
        <ArgonBox display="flex" justifyContent="center" p={5}>
          <CircularProgress />
        </ArgonBox>
      );
    }
    switch (activeTab) {
      case 0:
        return <PricingRules priceRules={priceRules} {...childProps} />;
      case 1:
        return <RevenueBilling {...childProps} />;
      case 2:
        return <Approval {...childProps} />;
      case 3:
        return <Tasks {...childProps} />;
      case 4:
        return <Quote quoteTemplates={quoteTemplates} {...childProps} />;
      default:
        return null;
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <Card>
          <ArgonBox
            display="flex"
            alignItems="center"
            flexWrap="wrap"
            p={2}
            sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
          >
            <ArgonBox width={{ xs: '100%', sm: 'auto' }} pr={{ xs: 0, sm: 2 }} mb={{ xs: 2, sm: 0 }}>
              <ArgonSelect
                placeholder="Select CPQ Organization..."
                value={selectedOrgId}
                onChange={handleOrgChange}
                options={orgOptions}
                disabled={loadingOrgs || isLoadingConfig}
              />
            </ArgonBox>
            <ArgonBox flexGrow={1}>
              <Tabs value={activeTab} onChange={handleTabChange} aria-label="configuration tabs">
                <Tab label="Pricing Rules" />
                <Tab label="Revenue Billing" />
                <Tab label="Approval" />
                <Tab label="Tasks" />
                <Tab label="Quote" />
              </Tabs>
            </ArgonBox>
          </ArgonBox>
          <ArgonBox p={3}>{renderActiveTab()}</ArgonBox>
        </Card>
      </ArgonBox>
    </DashboardLayout>
  );
}

export default Configuration;
